import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AccountService } from '../../authentication/_services/account.service';
import { DialogService } from '../../common/_services/dialog.service';

export interface LanguageInfo {
    code: string;
    name: string;
}

@Injectable({
    providedIn: 'root',
})
export class LanguageDetectionService {
    readonly SUPPORTED_LANGUAGES: LanguageInfo[] = [
        { code: 'fr', name: 'Français' },
        { code: 'en', name: 'English' },
        { code: 'de', name: 'Deutsch' },
        { code: 'es', name: 'Español' },
    ];

    readonly DEFAULT_LANGUAGE = 'en';
    readonly LANGUAGE_STORAGE_KEY = 'language';

    constructor(
        private accountService: AccountService,
        private dialogService: DialogService,
        private translateService: TranslateService,
    ) {}

    detectLanguage(): string {
        // 1. Check URL query parameter
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get('lang');

        if (langParam && this.isLanguageSupported(langParam)) {
            // Remove lang parameter from URL to keep it clean
            this.removeLanguageParamFromUrl();
            return langParam;
        }

        // 2. Check localStorage (existing preference)
        const storedLang = localStorage.getItem(this.LANGUAGE_STORAGE_KEY);
        if (storedLang && this.isLanguageSupported(storedLang)) {
            return storedLang;
        }

        // 3. Check browser language
        const browserLang = this.getBrowserLanguage();
        if (browserLang && this.isLanguageSupported(browserLang)) {
            return browserLang;
        }

        // 4. Default fallback
        return this.DEFAULT_LANGUAGE;
    }

    private isLanguageSupported(langCode: string): boolean {
        return this.SUPPORTED_LANGUAGES.some((lang) => lang.code === langCode);
    }

    private getBrowserLanguage(): string | null {
        if (typeof navigator === 'undefined') {
            return null;
        }

        const browserLang =
            navigator.language ||
            (navigator as { userLanguage?: string }).userLanguage;
        if (!browserLang) {
            return null;
        }

        // Extract the language code (e.g., 'fr-FR' -> 'fr')
        const langCode = browserLang.split('-')[0].toLowerCase();

        // Map browser language to supported language
        switch (langCode) {
            case 'fr':
                return 'fr';
            case 'en':
                return 'en';
            case 'de':
                return 'de';
            case 'es':
                return 'es';
            default:
                return null;
        }
    }

    private removeLanguageParamFromUrl(): void {
        const url = new URL(window.location.href);
        url.searchParams.delete('lang');

        // Update URL without reloading the page
        window.history.replaceState({}, '', url.toString());
    }

    getSupportedLanguages(): LanguageInfo[] {
        return this.SUPPORTED_LANGUAGES;
    }

    changeLanguage(langCode: string): void {
        if (this.isLanguageSupported(langCode)) {
            if (this.accountService.isLoggedIn()) {
                this.dialogService.ShowLoading('...');
                this.accountService.setLanguage(langCode).subscribe({
                    next: (success) => {
                        if (success) {
                            this.saveLanguagePreference(langCode);
                            window.location.reload();
                        } else {
                            this.dialogService.CloseLoading();
                        }
                    },
                    error: () => {
                        this.dialogService.CloseLoading();
                    },
                });
            } else {
                this.saveLanguagePreference(langCode);
                window.location.reload();
            }
        }
    }

    loadLanguageFromApi(): void {
        if (!this.accountService.isLoggedIn()) {
            return;
        }

        this.accountService.getLanguage().subscribe({
            next: (langCode) => {
                if (langCode && this.isLanguageSupported(langCode)) {
                    if (langCode !== this.translateService.currentLang) {
                        this.saveLanguagePreference(langCode);
                        this.translateService.use(langCode);
                        window.location.reload();
                    }
                }
            },
        });
    }

    saveLanguagePreference(langCode: string): void {
        if (this.isLanguageSupported(langCode)) {
            localStorage.setItem(this.LANGUAGE_STORAGE_KEY, langCode);
        }
    }
}
